import SectionHeader from "@/components/SectionHeader";
import NetsheetForm from "@/components/NetsheetForm";
import OpenHousePlanCTA from "@/components/OpenHousePlanCTA";
import { IMG, SITE } from "@/lib/api";
import { Link } from "react-router-dom";
import { Gauge, Laptop, MapPinned, Network, Smartphone, Send, Calculator, FileText, ShieldAlert, Tv, ArrowUpRight } from "lucide-react";

const PILLARS = [
  {
    Icon: Gauge,
    title: "Speed & Simplicity",
    desc: "A streamlined, digital-first process that helps you close faster — with fewer surprises and zero guesswork for you or your clients.",
  },
  {
    Icon: Laptop,
    title: "Fully Digital Closings",
    desc: "Offer your clients the convenience of Remote Online Notarization (RON) and fully digital closings — anytime, anywhere with VirtualClose®.",
  },
  {
    Icon: MapPinned,
    title: "Real-Time File Tracking",
    desc: "Stay in the loop with PowerSnap! — our mobile-friendly platform for file status, escrow management, and instant cost estimates.",
  },
  {
    Icon: Network,
    title: "Local Expertise, National Reach",
    desc: "Deep roots in your market with the backing of Anywhere Real Estate — the best of both worlds for your business and your clients.",
  },
];

const TOOLS = [
  {
    Icon: Calculator,
    title: "Mortgage Payment Calculator",
    desc: "Whether your clients are buying their first home or upgrading to something new, this helpful calculator helps you guide them through the numbers with confidence.",
    cta: "Generate an estimate",
    href: "https://www.westcoastescrow.com/calculators/",
  },
  {
    Icon: Smartphone,
    title: "In-App Messaging with PowerSnap!",
    desc: "Access quotes, calculators, and file updates on the go with PowerSnap! — our end-to-end mobile app that keeps your title and closing information at your fingertips.",
    cta: "Learn about PowerSnap!",
    href: "https://www.westcoastescrow.com/agents/powersnap/",
  },
  {
    Icon: Send,
    title: "Streamlined Earnest Money with ZOCCAM",
    desc: "Spend less time pedaling checks and more time with your clients. ZOCCAM streamlines the process of transferring earnest money — fast, secure, and mobile.",
    cta: "Get ZOCCAM",
    href: "https://apps.apple.com/us/app/zoccam/id911878116?mt=8",
  },
];

const RESOURCES = [
  {
    Icon: ShieldAlert,
    title: "Recognizing Red Flags & Preventing Wire Fraud",
    desc: "In 2023, the FBI logged a cybercrime every 28 seconds. Here's how to protect your transactions — and your clients.",
    href: "https://www.westcoastescrow.com/blog/stay-vigilant-recognizing-red-flags-and-preventing-wire-fraud_/",
  },
  {
    Icon: Tv,
    title: "Making TikTok Work for You",
    desc: "How agents are turning short-form video into their highest-converting lead source — without burning out.",
    href: "https://www.westcoastescrow.com/blog/making-tiktok-work-for-you/",
  },
  {
    Icon: FileText,
    title: "Understanding Probate",
    desc: "A plain-English walkthrough of the legal process when a homeowner passes — and what you, as the agent, need to know.",
    href: "https://www.westcoastescrow.com/blog/understanding-probate-the-legal-process-of-distributing-assets-after-death/",
  },
];

const TESTIMONIALS = [
  {
    quote: "We've been using Sonia and her team for years and very much benefit from her professionalism and quality of work. When searching for a quality escrow partner, look no further.",
    source: "5-Star Agent Review",
  },
  {
    quote: "I was impressed by Sonia's thoroughness and clear explanations about the escrow process. Always responsive, pleasant, and easy to talk with. A strong asset — I'd recommend her to anyone.",
    source: "5-Star Agent Review",
  },
  {
    quote: "I love working with Sonia. Very thorough, very knowledgeable. Always accommodating and helpful. GREAT escrow experience.",
    source: "5-Star Agent Review",
  },
];

export default function Escrow() {
  return (
    <div data-testid="escrow-page">
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden">
        <img src={IMG.interior1} alt="West Coast Escrow" className="absolute inset-0 w-full h-full object-cover img-warm" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/70" />
        <div className="relative h-full container-edge flex items-end pb-20">
          <div className="text-white max-w-3xl">
            <p className="overline text-[#e8c79a]">Escrow · For Agents</p>
            <h1 className="hero-headline mt-4 text-5xl md:text-6xl lg:text-7xl text-white" style={{ fontFamily: "Playfair Display, serif" }}>
              Empowering real<br />estate agents.
            </h1>
            <p className="mt-6 text-lg text-white/90 max-w-2xl font-light">
              Meet Sonia Rolland, our Regional Sales Director at West Coast Escrow. With 40+ years
              serving California agents and their clients, we know what matters most: faster
              closings, happier clients, and the kind of support that makes your job easier.
            </p>
            <Link to="/contact" className="btn-secondary mt-10" data-testid="escrow-hero-cta">
              Partner with Us
            </Link>
          </div>
        </div>
      </section>

      {/* Netsheet request (top of page) */}
      <section className="section" data-testid="escrow-netsheet">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionHeader
              overline="Netsheet"
              title="Need a quick estimate?"
              subtitle="Send us the basics and we'll prepare a netsheet for your transaction. Most requests turned around within one business day."
            />
            <div className="mt-8 space-y-2 text-sm text-soft">
              <p>Or email us directly:</p>
              <a href={SITE.escrowEmailHref} className="block text-ocean hover:text-sand transition-colors break-all" data-testid="netsheet-direct-email">
                {SITE.escrowEmail}
              </a>
              <a href={SITE.teamPhoneHref} className="block text-ocean hover:text-sand transition-colors">
                {SITE.teamPhone}
              </a>
            </div>
          </div>
          <div className="lg:col-span-8">
            <NetsheetForm />
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="section bg-linen" data-testid="escrow-intro">
        <SectionHeader
          overline="Your escrow partner"
          title="Faster closings, happier clients."
          subtitle="Agents and their clients rely on our experienced escrow team to guide transactions smoothly — with clear communication and trusted service from start to finish. From contract to close, we deliver precision, transparency, and peace of mind — so you can focus on your clients, not the paperwork."
        />
      </section>

      {/* Four pillars */}
      <section className="section bg-linen pt-0" data-testid="escrow-pillars">
        <div className="container-edge -mt-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {PILLARS.map(({ Icon, title, desc }, i) => (
              <div key={title} className="bg-paper border border-soft p-10 md:p-12" data-testid={`escrow-pillar-${i}`}>
                <Icon size={32} className="text-sand" strokeWidth={1.25} />
                <h3 className="mt-8 text-2xl md:text-3xl" style={{ fontFamily: "Playfair Display, serif" }}>{title}</h3>
                <p className="mt-4 text-soft leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section" data-testid="escrow-testimonials">
        <SectionHeader overline="What agents say" title="The escrow expert agents recommend." />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {TESTIMONIALS.map((t, i) => (
            <figure key={t.quote} className="border-l-2 border-soft pl-6" data-testid={`escrow-testimonial-${i}`}>
              <blockquote
                className="text-lg md:text-xl leading-snug"
                style={{ fontFamily: "Playfair Display, serif", fontStyle: "italic" }}
              >
                "{t.quote}"
              </blockquote>
              <figcaption className="overline mt-6">{t.source}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Open House Plan CTA */}
      <section className="section" data-testid="escrow-openhouse-section">
        <OpenHousePlanCTA />
      </section>

      {/* Tools */}
      <section className="section bg-linen" data-testid="escrow-tools">
        <SectionHeader
          overline="Tools"
          title="Tools that work as hard as you do."
          subtitle="We help real estate agents save time, reduce friction, and deliver a smoother client experience. With flexible closing options and digital tools built for speed, we eliminate the common transaction roadblocks — so you can focus on closing deals and building your business."
        />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {TOOLS.map(({ Icon, title, desc, cta, href }, i) => (
            <div key={title} className="bg-paper border border-soft p-10 flex flex-col" data-testid={`escrow-tool-${i}`}>
              <Icon size={28} className="text-sand" strokeWidth={1.25} />
              <h3 className="mt-8 text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>{title}</h3>
              <p className="mt-4 text-soft text-sm leading-relaxed flex-1">{desc}</p>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center text-sm text-ocean hover:text-sand transition-colors self-start"
                data-testid={`escrow-tool-${i}-link`}
              >
                {cta} <ArrowUpRight size={14} className="ml-2" />
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* Resources */}
      <section className="section" data-testid="escrow-resources">
        <SectionHeader overline="Resources" title="More to help you get started." subtitle="A curated library of articles for agents and the clients you serve." />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {RESOURCES.map(({ Icon, title, desc, href }, i) => (
            <a
              key={title}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="editorial-card p-10 flex flex-col group"
              data-testid={`escrow-resource-${i}`}
            >
              <Icon size={26} className="text-sand" strokeWidth={1.25} />
              <h3 className="mt-8 text-xl md:text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>{title}</h3>
              <p className="mt-4 text-soft text-sm leading-relaxed flex-1">{desc}</p>
              <span className="mt-8 inline-flex items-center text-sm text-ocean group-hover:text-sand transition-colors">
                Read article <ArrowUpRight size={14} className="ml-2" />
              </span>
            </a>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="section text-white"
        style={{ backgroundColor: "var(--primary)" }}
        data-testid="escrow-final-cta"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <p className="overline" style={{ color: "#e8c79a" }}>Let's work together</p>
            <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl text-white" style={{ fontFamily: "Playfair Display, serif" }}>
              From contract to close — we've got you.
            </h2>
            <p className="mt-6 text-white/80 max-w-xl">
              Reach out directly and let's talk through your next transaction. We'd love to be
              part of your team.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" className="btn-secondary" data-testid="escrow-contact-cta">
              Get in touch
            </Link>
            <p className="mt-6 text-white/85 text-sm">{SITE.teamPhone}</p>
            <p className="mt-1 text-white/85 text-sm break-all">
              <span className="block text-white/75 text-xs uppercase tracking-[0.18em] mb-1">Escrow</span>
              <a href={SITE.escrowEmailHref} className="hover:text-white transition-colors" data-testid="escrow-cta-escrow-email">{SITE.escrowEmail}</a>
            </p>
            <p className="mt-3 text-white/85 text-sm break-all">
              <span className="block text-white/75 text-xs uppercase tracking-[0.18em] mb-1">Real estate</span>
              <a href={SITE.emailHref} className="hover:text-white transition-colors">{SITE.email}</a>
            </p>
            <p className="mt-4 text-white/75 text-xs uppercase tracking-[0.18em]">Regional Sales Director · West Coast Escrow</p>
          </div>
        </div>
      </section>
    </div>
  );
}
